import router from './router'
import type { RouteRecordRaw } from 'vue-router'
import { useTitle } from '@/hooks/web/useTitle'
import { useNProgress } from '@/hooks/web/useNProgress'
import { usePermissionStoreWithOut } from '@/store/modules/permission'
import { usePageLoading } from '@/hooks/web/usePageLoading'
import { NO_REDIRECT_WHITE_LIST } from '@/constants'
import { useUserStoreWithOut } from '@/store/modules/user'
import { useMenuStore } from './store/modules/menu'

const { start, done } = useNProgress()
const { loadStart, loadDone } = usePageLoading()

const isServerRouteTree = (routes: unknown): routes is AppCustomRouteRecordRaw[] => {
  if (!Array.isArray(routes) || routes.length === 0) return false
  const first = routes[0] as Record<string, unknown>
  return !!first && typeof first === 'object' && ('component' in first || 'children' in first)
}

const normalizeAccessPath = (path: string) => {
  return String(path || '')
    .split('#')[0]
    .split('?')[0]
    .trim()
}

/**
 * 已登录状态下默认放行的静态路由
 *
 * 说明：
 * 1. 只用于前端本地静态路由调试或不受后端权限控制的页面
 * 2. 不影响未登录拦截逻辑，未登录访问仍然会跳登录页
 * 3. 如果后续后端已经配置 /base/questionBank，可以删除这里的调试路径
 */
const DEFAULT_NO_PERMISSION_PATHS = ['/questionBankDebug', '/questionBankDebug/index']

const isDefaultNoPermissionPath = (path: string) => {
  const target = normalizeAccessPath(path)

  return DEFAULT_NO_PERMISSION_PATHS.some((item) => {
    const current = normalizeAccessPath(item)
    return target === current || target.startsWith(`${current}/`)
  })
}

const hasAccessToPath = (menuPaths: string[], targetPath: string) => {
  const target = normalizeAccessPath(targetPath)
  return menuPaths.some((item) => {
    const current = normalizeAccessPath(item)
    return target === current || target.startsWith(`${current}/`)
  })
}

// 隐藏路由默认放行，不参与菜单权限拦截
const isHiddenResolvedRoute = (targetPath: string) => {
  const resolved = router.resolve(targetPath)
  return resolved.matched.some((record) => record.meta?.hidden === true)
}

const addRoutesToRouter = (routes: AppRouteRecordRaw[]) => {
  routes.forEach((route) => {
    const routeName = (route as any)?.name
    if (routeName && !router.hasRoute(routeName)) {
      router.addRoute(route as unknown as RouteRecordRaw)
    }
  })
}

const restorePersistedDynamicRoutes = () => {
  const permissionStore = usePermissionStoreWithOut()
  const persistedRoutes = permissionStore.getAddRouters || []

  if (!Array.isArray(persistedRoutes) || persistedRoutes.length === 0) {
    return false
  }

  addRoutesToRouter(persistedRoutes)
  permissionStore.setIsAddRouters(true)
  return true
}

let routeInitPromise: Promise<boolean> | null = null

const ensureDynamicRoutesReady = async () => {
  const permissionStore = usePermissionStoreWithOut()
  const userStore = useUserStoreWithOut()

  if (permissionStore.getIsAddRouters && permissionStore.getAddRouters.length > 0) {
    return true
  }

  if (routeInitPromise) {
    return routeInitPromise
  }

  routeInitPromise = (async () => {
    // 先尝试恢复本地持久化的动态路由，解决“刷新后 router 实例丢失动态路由”的问题
    if (restorePersistedDynamicRoutes()) {
      return true
    }

    const roleRouters = userStore.getRoleRouters || []

    // 没有权限路由时，不要生成空动态路由，更不要提前标记已初始化
    if (!Array.isArray(roleRouters) || roleRouters.length === 0) {
      return false
    }

    permissionStore.resetPermissionState()

    if (isServerRouteTree(roleRouters)) {
      await permissionStore.generateRoutes('server', roleRouters)
    } else {
      await permissionStore.generateRoutes('frontEnd', roleRouters as string[])
    }

    addRoutesToRouter(permissionStore.getAddRouters)
    permissionStore.setIsAddRouters(true)

    return true
  })().finally(() => {
    routeInitPromise = null
  })

  return routeInitPromise
}

const getRedirectAfterRouteReady = (to: any, from: any) => {
  const userStore = useUserStoreWithOut()
  const roleRouters = userStore.getRoleRouters || []

  let redirect = decodeURIComponent((from.query.redirect || to.fullPath || to.path) as string)
  const hiddenRouteAllowed = isHiddenResolvedRoute(redirect)

  if (Array.isArray(roleRouters) && roleRouters.length > 0 && typeof roleRouters[0] === 'string') {
    const menuPaths = roleRouters as string[]
    if (!hasAccessToPath(menuPaths, redirect) && !hiddenRouteAllowed) {
      redirect = menuPaths[0]
    }
  }

  return redirect
}

router.beforeEach(async (to, from, next) => {
  const menuStore = useMenuStore()
  menuStore.setPage(to.path)

  start()
  loadStart()

  const permissionStore = usePermissionStoreWithOut()
  const userStore = useUserStoreWithOut()

  if (userStore.getToken) {
    // 这里不能无脑拦截 /login，否则 token 还在但权限路由丢失时会产生来回跳转
    if (to.path === '/login') {
      const hasSavedRoutes =
        Array.isArray(permissionStore.getAddRouters) && permissionStore.getAddRouters.length > 0
      const hasRoleRouters =
        Array.isArray(userStore.getRoleRouters) && userStore.getRoleRouters.length > 0

      if (hasSavedRoutes || hasRoleRouters) {
        next({ path: '/' })
      } else {
        next()
      }
      return
    }

    /**
     * 默认不受后端权限控制的静态路由
     *
     * 放在 needInitRoutes 前面，避免动态权限路由未初始化时，
     * 静态调试页面被误判为权限路由缺失而跳登录。
     */
    if (isDefaultNoPermissionPath(to.path)) {
      next()
      return
    }

    const needInitRoutes =
      !permissionStore.getIsAddRouters || permissionStore.getAddRouters.length === 0

    if (needInitRoutes) {
      const ready = await ensureDynamicRoutesReady()

      // 权限路由确实拿不到时，不再误进 404，直接回登录页
      if (!ready) {
        permissionStore.resetPermissionState()
        next(`/login?redirect=${encodeURIComponent(to.fullPath)}`)
        return
      }

      const redirect = getRedirectAfterRouteReady(to, from)
      const nextData = to.fullPath === redirect ? { ...to, replace: true } : { path: redirect }
      next(nextData)
      return
    }

    next()
    return
  }

  if (NO_REDIRECT_WHITE_LIST.indexOf(to.path) !== -1) {
    next()
  } else {
    next(`/login?redirect=${encodeURIComponent(to.path)}`)
  }
})

router.afterEach((to) => {
  useTitle(to?.meta?.title as string)
  done()
  loadDone()
})
